import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import '../backlog/backlog.css';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Default Settings → Date Filter  (legacy "Date Filter - Category")
//
// TEN workflow categories, each a radio of date ranges (1M · 3M · 6M · 1Y · 2Y · 5Y ·
// All). The values are the GLOBAL default date range per workflow, stored in the ten
// category columns of the shared dbo.DefaultSetting anchor row — a change here changes
// ALPACA too (CLAUDE.md §5.0). Service/admin only. Layout mirrors the legacy form:
// two columns of five categories, label beside its radio group.
// ─────────────────────────────────────────────────────────────────────────────

interface Choice { value: number; code: string; label: string }
interface Category { key: string; label: string; value: number }
interface Cfg { anchorOid: string; choices: Choice[]; categories: Category[] }

const FALLBACK_CHOICES: Choice[] = [
  { value: 0, code: '1M', label: '1 Month' }, { value: 1, code: '3M', label: '3 Months' },
  { value: 2, code: '6M', label: '6 Months' }, { value: 3, code: '1Y', label: '1 Year' },
  { value: 4, code: '2Y', label: '2 Years' }, { value: 5, code: '5Y', label: '5 Years' },
  { value: 6, code: 'All', label: 'All' },
];

export default function DateFilterPage() {
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [values, setValues] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: Cfg }>('/api/v1/date-filter');
      if (res.data.success) {
        setCfg(res.data.data);
        setValues(Object.fromEntries(res.data.data.categories.map((c) => [c.key, c.value])));
      }
    } catch { notify.error('Could not load Date Filter.'); }
    finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const choices = cfg?.choices?.length ? cfg.choices : FALLBACK_CHOICES;
  const cats = cfg?.categories ?? [];
  const left = cats.slice(0, 5);
  const right = cats.slice(5);

  // gate.cancel(revert): revert the values AND drop back to read-only, else the
  // panel stays in edit mode and Cancel appears to do nothing (owner report 2026-10-12).
  const handleCancel = () => gate.cancel(() => {
    if (cfg) setValues(Object.fromEntries(cfg.categories.map((c) => [c.key, c.value])));
  });

  const handleSave = () => {
    if (!cfg) return;
    setSaving(true);
    void gate.save({
      formName: 'Date Filter',
      successMessage: 'Date Filter saved successfully.',
      save: async () => { await api.put('/api/v1/date-filter', { anchorOid: cfg.anchorOid, values }); },
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change Date Filter.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  const codeOf = useMemo(() => {
    const m = new Map(choices.map((c) => [c.value, c.code]));
    return (v: number) => m.get(v) ?? String(v);
  }, [choices]);

  const CategoryRow = ({ cat }: { cat: Category }) => (
    <div className="df-row">
      <label className="df-label">{cat.label}:</label>
      {ro ? (
        <span className="df-ro">{codeOf(values[cat.key] ?? cat.value)}</span>
      ) : (
        <div className="df-radios">
          {choices.map((ch) => (
            <label key={ch.value} className="df-radio" title={ch.label}>
              <input
                type="radio"
                name={`df-${cat.key}`}
                checked={(values[cat.key] ?? cat.value) === ch.value}
                onChange={() => setValues((s) => ({ ...s, [cat.key]: ch.value }))}
                style={{ accentColor: 'var(--ai-orange)' }}
              />
              {ch.code}
            </label>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bl-page">
      <style>{`
        .df-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 48px; max-width: 1150px; }
        @media (max-width: 900px) { .df-grid { grid-template-columns: 1fr; } }
        .df-row { display: flex; align-items: center; gap: 12px; padding: 6px 0; }
        .df-label { min-width: 160px; font-size: 13px; font-weight: 600; color: var(--ai-ink); }
        /* Each category's radio group is boxed in its own border (legacy parity, image 225). */
        .df-radios { display: flex; flex-wrap: wrap; gap: 4px 14px; flex: 1; border: 1px solid var(--ai-line);
          border-radius: 0; padding: 6px 12px; background: #fff; }
        .df-radio { display: inline-flex; align-items: center; gap: 4px; font-size: 12.5px; color: var(--ai-ink); cursor: pointer; }
        .df-ro { flex: 1; font-size: 13px; font-weight: 600; color: var(--ai-ink); border: 1px solid var(--ai-line);
          border-radius: 0; padding: 6px 12px; background: #fff; }
      `}</style>
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Default Settings</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Date Filter</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="datefilter" auditFormName="Date Filter" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            The default date range each workflow's list views open with. Shared with ALPACA — a change here applies to both products.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<EditGateActions gate={gate} saving={saving} disabled={loading}
              onSave={handleSave} onCancel={handleCancel} />}
            onRefresh={() => void load()} refreshing={loading} refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 28px' }}>
            {cats.length === 0 ? (
              <div style={{ color: 'var(--ai-muted)', fontSize: 13 }}>{loading ? 'Loading…' : 'No categories.'}</div>
            ) : (
              <div className="df-grid">
                <div>{left.map((c) => <CategoryRow key={c.key} cat={c} />)}</div>
                <div>{right.map((c) => <CategoryRow key={c.key} cat={c} />)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
